import { v4 as uniqid } from "uuid"

/**
 * app.state.missionChange is the notice open project screens follow. It used to carry one
 * change, so a batch of changes had to be published one notice (and one render) at a time.
 * It now carries `entries`: every change in the batch, each shaped like the old single
 * notice ({ for, change, obj, missionId, from, ... }). The notice's own top-level fields
 * are the last entry's, so screens that only refresh on a new timestamp need no change.
 *
 * Screens that act on what changed read missionChangeEntries(notice) and handle each one.
 */

/**
 * One notice for a batch of changes
 * @param entries {object[]} Changes, oldest first, shaped like a single notice
 */
export function buildMissionChange(entries) {
    const list = (entries || []).filter(Boolean)
    const last = list[list.length - 1] || {}

    return { ...last, entries: list, timestamp: uniqid() }
}

/**
 * Every change a notice carries, oldest first. A notice published the old way (no
 * entries) is a batch of one; an empty notice ({}) is a batch of none.
 */
export function missionChangeEntries(notice) {
    if (!notice) return []
    if (Array.isArray(notice.entries)) return notice.entries

    // Task grid cells run this per cell per notice, so no copying: any field but the
    // timestamp makes it a real change
    for (const key in notice) {
        if (key !== "timestamp") return [notice]
    }

    return []
}

/**
 * True when every change in the notice names a project and none of them is missionId,
 * so a screen showing missionId can skip it. A change that names no project could be
 * anything, so it never lets the screen skip.
 */
export function missionChangeIsOnlyOtherMissions(notice, missionId) {
    const entries = missionChangeEntries(notice)

    return entries.length > 0 && entries.every((e) => e.missionId && e.missionId !== missionId)
}
